import axios from 'axios';

const getAuthToken = () => {
  const token =
    localStorage.getItem('token') || sessionStorage.getItem('token');
  return token;
};

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_AXIOS_BASE_URL || '';
  return baseUrl ? `${baseUrl}${endpoint}` : endpoint;
};

export const uploadSyllabus = async (courseId, file) => {
  return uploadSyllabusWithProgress(file, courseId, getAuthToken());
};

const parseJsonSafe = (value) => {
  if (!value) {
    return {};
  }

  try {
    return JSON.parse(value);
  } catch {
    return { message: value };
  }
};

const isRetryAnalysisPayload = (payload = {}) => {
  if (payload?.retry === true || payload?.isRetry === true) {
    return true;
  }

  const attempt = Number(payload?.attempt || payload?.retryAttempt);
  if (Number.isFinite(attempt) && attempt > 1) {
    return true;
  }

  const status = String(payload?.status || payload?.phase || '').toLowerCase();
  return status.includes('retry');
};

const parseSseChunk = (rawChunk, onEvent) => {
  const frames = rawChunk.split(/\r?\n\r?\n/).filter(Boolean);

  for (const frame of frames) {
    const lines = frame.split(/\r?\n/);
    let event = 'message';
    const dataLines = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        event = line.replace('event:', '').trim() || 'message';
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.replace('data:', '').trim());
      }
    }

    const data = dataLines.join('\n');
    if (!data) {
      continue;
    }

    onEvent(event, parseJsonSafe(data));
  }
};

const resolveNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') {
      continue;
    }

    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const getNormalizedSyllabusResponse = (rawPayload) => {
  const payload = rawPayload?.payload || rawPayload || {};

  return {
    syllabus: payload?.syllabus,
    rows: payload?.rows || payload?.syllabus?.rows || [],
    ...payload,
  };
};

const getLastSseBoundary = (text = '') => {
  const lfBoundary = text.lastIndexOf('\n\n');
  const crlfBoundary = text.lastIndexOf('\r\n\r\n');

  if (crlfBoundary > lfBoundary) {
    return { index: crlfBoundary, length: 4 };
  }

  if (lfBoundary !== -1) {
    return { index: lfBoundary, length: 2 };
  }

  return { index: -1, length: 0 };
};

export const uploadSyllabusWithProgress = async (
  file,
  courseId,
  token,
  onProgress,
  guidance,
) => {
  const formData = new FormData();
  formData.append('courseId', courseId);
  formData.append('file', file);
  formData.append('streamUpdates', 'true');
  if (guidance) formData.append('guidance', guidance);

  let processedLength = 0;
  let responseBuffer = '';
  let completePayload = null;
  let streamError = null;

  const handleEvent = (eventName, payload) => {
    const nextProgressPercent = resolveNumber(
      payload?.progressPercent,
      payload?.progress,
      payload?.percent,
    );

    const nextEtaSeconds = resolveNumber(
      payload?.estimatedSecondsRemaining,
      payload?.etaSeconds,
      payload?.secondsRemaining,
      payload?.estimatedTimeRemainingSeconds,
    );

    if (eventName === 'started') {
      onProgress?.({
        eventName,
        currentStep:
          payload?.label ||
          payload?.currentStep ||
          payload?.step ||
          'Starting upload',
        ...(nextProgressPercent !== undefined
          ? { progressPercent: nextProgressPercent }
          : {}),
        ...(nextEtaSeconds !== undefined
          ? { estimatedSecondsRemaining: nextEtaSeconds }
          : {}),
      });
      return;
    }

    if (eventName === 'progress') {
      onProgress?.({
        eventName: payload?.eventName || payload?.event || eventName,
        currentStep:
          payload?.label || payload?.currentStep || payload?.step || '',
        isRetryAnalysis: isRetryAnalysisPayload(payload),
        ...(nextProgressPercent !== undefined
          ? { progressPercent: nextProgressPercent }
          : {}),
        ...(nextEtaSeconds !== undefined
          ? { estimatedSecondsRemaining: nextEtaSeconds }
          : {}),
      });
      return;
    }

    if (eventName === 'error') {
      streamError = new Error(
        payload?.message || payload?.error || 'Failed to upload syllabus.',
      );
      return;
    }

    if (eventName === 'complete') {
      completePayload = getNormalizedSyllabusResponse(payload);
      return;
    }

    onProgress?.({
      eventName,
      currentStep:
        payload?.label || payload?.currentStep || payload?.step || '',
      isRetryAnalysis: isRetryAnalysisPayload(payload),
      ...(nextProgressPercent !== undefined
        ? { progressPercent: nextProgressPercent }
        : {}),
      ...(nextEtaSeconds !== undefined
        ? { estimatedSecondsRemaining: nextEtaSeconds }
        : {}),
    });
  };

  try {
    const response = await axios.post(
      getApiUrl('/api/courseSyllabus/uploadSyllabus'),
      formData,
      {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token || getAuthToken()}`,
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'text',
        transitional: { forcedJSONParsing: false },
        onDownloadProgress: (progressEvent) => {
          const xhr = progressEvent?.event?.target || progressEvent?.target;
          const fullText = xhr?.responseText;

          if (typeof fullText !== 'string') {
            return;
          }

          const newText = fullText.slice(processedLength);
          processedLength = fullText.length;
          responseBuffer += newText;

          const { index: lastFrameBoundary, length: boundaryLength } =
            getLastSseBoundary(responseBuffer);

          if (lastFrameBoundary === -1) {
            return;
          }

          const completePart = responseBuffer.slice(
            0,
            lastFrameBoundary + boundaryLength,
          );
          responseBuffer = responseBuffer.slice(
            lastFrameBoundary + boundaryLength,
          );

          parseSseChunk(completePart, handleEvent);
        },
      },
    );

    if (streamError) {
      throw streamError;
    }

    const contentType = String(
      response.headers?.['content-type'] || '',
    ).toLowerCase();
    const isEventStream = contentType.includes('text/event-stream');

    if (!isEventStream) {
      const parsed =
        typeof response.data === 'string'
          ? parseJsonSafe(response.data)
          : response.data;

      return getNormalizedSyllabusResponse(parsed);
    }

    if (responseBuffer.trim()) {
      parseSseChunk(`${responseBuffer}\n\n`, handleEvent);
    }

    if (streamError) {
      throw streamError;
    }

    if (completePayload) {
      return completePayload;
    }

    throw new Error('Upload stream ended before completion.');
  } catch (error) {
    let message = error?.message || 'Failed to upload syllabus.';
    const errorData = error?.response?.data;

    if (typeof errorData === 'string' && errorData) {
      const payload = parseJsonSafe(errorData);
      message =
        payload?.message || payload?.error || payload?.detail || errorData;
    } else if (errorData && typeof errorData === 'object') {
      message =
        errorData?.message || errorData?.error || errorData?.detail || message;
    }

    throw new Error(message);
  }
};

export const createDraftSyllabus = async (courseId, rows) => {
  const response = await axios.post(
    '/api/courseSyllabus/createDraft',
    { courseId, rows },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const getSyllabus = async (courseId) => {
  const response = await axios.get('/api/courseSyllabus/getSyllabus', {
    params: { courseId },
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
  });
  return response.data;
};

export const addSyllabusRow = async (courseId, row) => {
  const response = await axios.post(
    '/api/courseSyllabus/addRow',
    { courseId, row },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const updateSyllabusRow = async (courseId, rowId, row) => {
  const response = await axios.post(
    '/api/courseSyllabus/updateRow',
    { courseId, rowId, row },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const deleteSyllabusRow = async (courseId, rowId) => {
  const response = await axios.post(
    '/api/courseSyllabus/deleteRow',
    { courseId, rowId },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const updateSyllabusSettings = async (courseId, settings) => {
  const response = await axios.post(
    '/api/courseSyllabus/updateSettings',
    { courseId, ...settings },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const updateSyllabusStatus = async (courseId, enabled) => {
  const response = await axios.post(
    '/api/courseSyllabus/updateStatus',
    { courseId, enabled },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const deleteSyllabus = async (courseId) => {
  const response = await axios.post(
    '/api/courseSyllabus/deleteSyllabus',
    { courseId },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const updateRowStatus = async (courseId, rowId, status) => {
  const response = await axios.post(
    '/api/courseSyllabus/updateRowStatus',
    { courseId, rowId, status },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const updateReadingStatus = async (
  courseId,
  rowId,
  readingId,
  status,
) => {
  const response = await axios.post(
    '/api/courseSyllabus/updateReadingStatus',
    { courseId, rowId, readingId, status },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const getRow = async (courseId, rowId) => {
  const response = await axios.get('/api/courseSyllabus/getRow', {
    params: { courseId, rowId },
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
  });
  return response.data;
};

export const generateReadings = async (
  courseId,
  rowId,
  searchNote,
  sources,
) => {
  const response = await axios.post(
    '/api/courseSyllabus/generateReadings',
    { courseId, rowId, searchNote, sources },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
    },
  );
  return response.data;
};

export const generateReadingsWithSSE = async (
  courseId,
  rowId,
  { searchNote, sourceIds, onProgress, signal } = {},
) => {
  let processedLength = 0;
  let responseBuffer = '';
  let completePayload = null;
  let streamError = null;

  const handleEvent = (eventName, payload) => {
    if (eventName === 'complete') {
      completePayload = { success: true, status: payload.status, skippedReason: payload.skippedReason || '' };
      return;
    }
    if (eventName === 'error') {
      streamError = new Error(payload.error);
      return;
    }
    onProgress?.({ type: eventName, ...payload });
  };

  const response = await axios.post(
    getApiUrl('/api/courseSyllabus/generateReadings'),
    {
      courseId,
      rowId,
      sendNotification: true,
      ...(searchNote ? { searchNote } : {}),
      ...(sourceIds?.length ? { sourceIds } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      responseType: 'text',
      transitional: { forcedJSONParsing: false },
      signal,
      onDownloadProgress: (progressEvent) => {
        const xhr = progressEvent?.event?.target || progressEvent?.target;
        const fullText = xhr?.responseText;

        if (typeof fullText !== 'string') return;

        const newText = fullText.slice(processedLength);
        processedLength = fullText.length;
        responseBuffer += newText;

        const { index: lastFrameBoundary, length: boundaryLength } =
          getLastSseBoundary(responseBuffer);

        if (lastFrameBoundary === -1) return;

        const completePart = responseBuffer.slice(
          0,
          lastFrameBoundary + boundaryLength,
        );
        responseBuffer = responseBuffer.slice(
          lastFrameBoundary + boundaryLength,
        );
        parseSseChunk(completePart, handleEvent);
      },
    },
  );

  if (streamError) throw streamError;

  const contentType = String(
    response.headers?.['content-type'] || '',
  ).toLowerCase();

  if (!contentType.includes('text/event-stream')) {
    const parsed =
      typeof response.data === 'string'
        ? parseJsonSafe(response.data)
        : response.data;
    return { success: true, status: parsed?.status || 'completed' };
  }

  if (responseBuffer.trim()) {
    parseSseChunk(`${responseBuffer}\n\n`, handleEvent);
  }

  if (streamError) throw streamError;
  if (completePayload) return completePayload;

  return { success: true, status: 'completed' };
};
